import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import GlobalOrderNotification from "../components/GlobalOrderNotification";
import { formatTrackHubError } from "../utils/formatTrackHubError";
import { ensureValidSession, getAccessToken, getRefreshToken } from "../services/authSession";
import {
  WS_RECONNECT,
  buildAuthenticatedWsUrl,
  getReconnectDelay,
  isAuthCloseCode,
  respondToServerPing,
} from "../utils/websocketPersistence";
import {
  isOrderSurfacePath,
  ORDERS_SURFACE_ACTIVE_EVENT,
} from "../constants/orderSurfaces";

const OrderNotificationContext = createContext();

/** Prominent new-order toast + alarm; mutually exclusive with update/timeout/inventory banners. */
const isHighPriorityNewOrderPayload = (order) =>
  !!order &&
  !order.is_order_updated &&
  !order.is_timeout &&
  !order.is_inventory_warning;

const buildOrderUpdatedPayload = (message) => ({
  ...(message.data?.order || {}),
  external_id:
    message.data?.external_id ||
    message.data?.order_id ||
    message.data?.jahez_id ||
    message.data?._id ||
    message.data?.chefz_order_id ||
    message.data?.jahez_order_id ||
    message.data?.keeta_order_id ||
    message.data?.keeta_id ||
    message.data?.order?.ninja_order_id,
  is_order_updated: true,
  update_platform: message.data?.platform,
  update_reason: message.data?.reason || message.data?.update_reason,
  update_timestamp: message.timestamp,
  new_status: message.data?.new_status,
  previous_status: message.data?.previous_status,
  suppress_toast: Boolean(message.data?.suppress_toast),
  trackhub: message.data?.trackhub,
});

const resolveLocalizedWsMessage = (data, language) => {
  const isAr = String(language || "").toLowerCase().startsWith("ar");
  if (isAr && data?.message_ar) return data.message_ar;
  return data?.message || data?.message_en || "";
};

export const useOrderNotification = () => {
  const context = useContext(OrderNotificationContext);
  if (!context) {
    throw new Error(
      "useOrderNotification must be used within OrderNotificationProvider"
    );
  }
  return context;
};

export const OrderNotificationProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [newOrder, setNewOrder] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [orderRefreshTrigger, setOrderRefreshTrigger] = useState(0);
  const [triggerOrderPopup, setTriggerOrderPopup] = useState(null);
  const [notificationMountKey, setNotificationMountKey] = useState(0);
  const audioRef = useRef(null);
  const location = useLocation();
  const WS_URL = process.env.REACT_APP_WS_URL || "ws://localhost:5001";
  const i18nLanguageRef = useRef(i18n.language);

  useEffect(() => {
    i18nLanguageRef.current = i18n.language;
  }, [i18n.language]);

  const [wsKey, setWsKey] = useState(0);
  const reconnectCountRef = useRef(0);
  const connectionTimeRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const recoveryTimeoutRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const pendingReconnectRef = useRef(false);
  const connectGenerationRef = useRef(0);
  const isPageVisibleRef = useRef(
    typeof document === "undefined" ? true : document.visibilityState === "visible",
  );

  const isLoggedIn = useCallback(
    () => !!(getAccessToken() || getRefreshToken()),
    [],
  );

  const clearReconnectTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (recoveryTimeoutRef.current) {
      clearTimeout(recoveryTimeoutRef.current);
      recoveryTimeoutRef.current = null;
    }
  }, []);

  const forceReconnect = useCallback(() => {
    setWsKey((prev) => prev + 1);
  }, []);

  const ensureConnection = useCallback(async () => {
    if (!isLoggedIn()) {
      return false;
    }

    clearReconnectTimers();
    reconnectCountRef.current = 0;
    pendingReconnectRef.current = false;

    const ok = await ensureValidSession();
    if (ok) {
      forceReconnect();
    }
    return ok;
  }, [clearReconnectTimers, forceReconnect, isLoggedIn]);

  const scheduleReconnect = useCallback(
    (options = {}) => {
      if (!isLoggedIn()) {
        return;
      }

      if (!isPageVisibleRef.current) {
        pendingReconnectRef.current = true;
        return;
      }

      clearReconnectTimers();

      const runReconnect = async () => {
        if (!isLoggedIn() || !isPageVisibleRef.current) {
          pendingReconnectRef.current = true;
          return;
        }

        if (options.refreshSession) {
          const ok = await ensureValidSession();
          if (!ok) {
            return;
          }
        }

        forceReconnect();
      };

      reconnectCountRef.current += 1;

      if (reconnectCountRef.current > WS_RECONNECT.MAX_ATTEMPTS) {
        console.error(
          `[GLOBAL] WebSocket max reconnect attempts (${WS_RECONNECT.MAX_ATTEMPTS}), scheduling recovery`,
        );
        recoveryTimeoutRef.current = setTimeout(() => {
          recoveryTimeoutRef.current = null;
          reconnectCountRef.current = 0;
          void runReconnect();
        }, WS_RECONNECT.RECOVERY_DELAY_MS);
        return;
      }

      const delay = options.authRetry
        ? WS_RECONNECT.AUTH_RETRY_DELAY_MS
        : getReconnectDelay(reconnectCountRef.current);

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        void runReconnect();
      }, delay);
    },
    [clearReconnectTimers, forceReconnect, isLoggedIn],
  );

  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem("orderNotificationSound");
    return saved !== null ? saved === "true" : true;
  });

  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  const notificationQueueRef = useRef([]);
  const showNotificationRef = useRef(showNotification);
  const newOrderRef = useRef(newOrder);

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    newOrderRef.current = newOrder;
  }, [newOrder]);

  const bumpNotificationMount = useCallback(() => {
    setNotificationMountKey((k) => k + 1);
  }, []);

  const playNewOrderSound = useCallback(() => {
    if (!soundEnabledRef.current || !audioRef.current) return;
    console.log("🔊 [GLOBAL] Playing notification sound...");
    audioRef.current.loop = true;
    audioRef.current.play().catch((err) => {
      console.error("❌ [GLOBAL] Audio play failed:", err);
    });
  }, []);

  const advanceQueueAfterCurrent = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
    const next = notificationQueueRef.current.shift();
    if (!next) {
      setShowNotification(false);
      setNewOrder(null);
      return;
    }
    bumpNotificationMount();
    setNewOrder(next);
    setShowNotification(true);
  }, [bumpNotificationMount]);

  useEffect(() => {
    const handleLogin = () => {
      console.log("🔄 [GLOBAL] Login event detected - reconnecting WebSocket");
      reconnectCountRef.current = 0;
      pendingReconnectRef.current = false;
      forceReconnect();
    };

    const handleSessionRefreshed = () => {
      console.log("🔄 [GLOBAL] Session refreshed - reconnecting WebSocket");
      reconnectCountRef.current = 0;
      forceReconnect();
    };

    const handleVisibilityChange = () => {
      const visible = document.visibilityState === "visible";
      isPageVisibleRef.current = visible;

      if (visible) {
        clearReconnectTimers();
        reconnectCountRef.current = 0;
        pendingReconnectRef.current = false;
        ensureValidSession().finally(() => {
          forceReconnect();
        });
      }
    };

    const handleOnline = () => {
      if (!isLoggedIn()) return;
      clearReconnectTimers();
      reconnectCountRef.current = 0;
      ensureValidSession().finally(() => {
        forceReconnect();
      });
    };

    const handleLogout = () => {
      console.log("🔌 [GLOBAL] Logout detected - closing WebSocket");
      intentionalCloseRef.current = true;
      clearReconnectTimers();
      reconnectCountRef.current = 0;
      pendingReconnectRef.current = false;
      forceReconnect();
    };

    const handleOrdersSurfaceActive = () => {
      if (!isOrderSurfacePath(window.location.pathname)) return;
      void ensureConnection();
    };

    window.addEventListener("userLogin", handleLogin);
    window.addEventListener("userLogout", handleLogout);
    window.addEventListener("sessionRefreshed", handleSessionRefreshed);
    window.addEventListener("online", handleOnline);
    window.addEventListener(ORDERS_SURFACE_ACTIVE_EVENT, handleOrdersSurfaceActive);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("userLogin", handleLogin);
      window.removeEventListener("userLogout", handleLogout);
      window.removeEventListener("sessionRefreshed", handleSessionRefreshed);
      window.removeEventListener(ORDERS_SURFACE_ACTIVE_EVENT, handleOrdersSurfaceActive);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [clearReconnectTimers, forceReconnect, isLoggedIn, ensureConnection]);

  useEffect(() => {
    if (!isOrderSurfacePath(location.pathname) || !isLoggedIn()) {
      return;
    }
    void ensureConnection();
  }, [location.pathname, isLoggedIn, ensureConnection]);

  useEffect(() => {
    console.log("🔌 [GLOBAL] WebSocket effect triggered, wsKey:", wsKey);

    clearReconnectTimers();

    if (!isLoggedIn()) {
      return () => {};
    }

    audioRef.current = new Audio("/notification.mp3");
    audioRef.current.loop = true;

    const generation = ++connectGenerationRef.current;
    intentionalCloseRef.current = false;
    let ws;

    const connectWebSocket = async () => {
      try {
        const sessionOk = await ensureValidSession();
        if (!sessionOk || generation !== connectGenerationRef.current) {
          console.log("⚠️ [GLOBAL] WebSocket skipped: invalid session or stale attempt");
          return;
        }

        if (!isPageVisibleRef.current) {
          pendingReconnectRef.current = true;
          return;
        }

        const token = getAccessToken() || localStorage.getItem("authToken");
        if (!token || generation !== connectGenerationRef.current) {
          console.log("⚠️ [GLOBAL] WebSocket skipped: no access token");
          return;
        }

        const wsUrl = buildAuthenticatedWsUrl(WS_URL, token);
        if (!wsUrl) return;

        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          if (generation !== connectGenerationRef.current) return;
          connectionTimeRef.current = Date.now();
          reconnectCountRef.current = 0;
          pendingReconnectRef.current = false;
          console.log("✅ [GLOBAL] Connected to WebSocket successfully");
        };

        ws.onmessage = (event) => {
          console.log("📨 [GLOBAL] Received WebSocket message:", event.data);
          try {
            const message = JSON.parse(event.data);
            console.log("📨 [GLOBAL] Parsed message:", message);

            if (message.type === "CONNECTED") {
              console.log("✅ [GLOBAL] WebSocket handshake complete");
              return;
            }

            if (message.type === "PING") {
              respondToServerPing(ws, message);
              return;
            }

            if (message.type === "NEW_ORDER") {
          console.log("🆕 [GLOBAL] NEW_ORDER detected - showing notification");

          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }

          if (
            showNotificationRef.current &&
            newOrderRef.current &&
            !isHighPriorityNewOrderPayload(newOrderRef.current)
          ) {
            notificationQueueRef.current.unshift({
              ...newOrderRef.current,
            });
            console.log(
              "📌 [GLOBAL] Preempted lower-priority toast; queued / will show NEW_ORDER now"
            );
          }

          setNewOrder(message.data);
          setShowNotification(true);
          bumpNotificationMount();

          setOrderRefreshTrigger((prev) => prev + 1);

          if (!soundEnabledRef.current) {
            console.log("🔇 [GLOBAL] Notification sound is disabled by user");
          } else if (audioRef.current) {
            playNewOrderSound();
          }

          return;
        }

        if (message.type === "ORDER_UPDATED") {
          console.log("📦 [GLOBAL] ORDER_UPDATED detected - triggering refresh");
          const updatedOrder = buildOrderUpdatedPayload(message);

          setOrderRefreshTrigger((prev) => prev + 1);

          if (updatedOrder.suppress_toast) {
            return;
          }

          if (
            showNotificationRef.current &&
            isHighPriorityNewOrderPayload(newOrderRef.current)
          ) {
            notificationQueueRef.current.push(updatedOrder);
            console.log(
              "📋 [GLOBAL] ORDER_UPDATED queued while NEW_ORDER is visible, depth:",
              notificationQueueRef.current.length
            );
            return;
          }

          bumpNotificationMount();
          setNewOrder(updatedOrder);
          setShowNotification(true);
          return;
        }

        if (message.type === "NEW_ORDER_AUTO_ACCEPT_FAILED") {
          console.log(
            "⚠️ [GLOBAL] NEW_ORDER_AUTO_ACCEPT_FAILED - showing notification"
          );
          const failedOrder = {
            ...(message.data?.order || message.data || {}),
            external_id:
              message.data?.external_id ||
              message.data?.order_id ||
              message.data?.order?.order_id ||
              message.data?._id,
            is_order_updated: true,
            update_platform: message.data?.platform,
            update_reason:
              message.data?.reason ||
              message.data?.update_reason ||
              "Auto-accept failed",
            new_status: message.data?.new_status || message.data?.status,
          };

          setOrderRefreshTrigger((prev) => prev + 1);

          if (
            showNotificationRef.current &&
            isHighPriorityNewOrderPayload(newOrderRef.current)
          ) {
            notificationQueueRef.current.push(failedOrder);
            return;
          }

          bumpNotificationMount();
          setNewOrder(failedOrder);
          setShowNotification(true);
          return;
        }

        if (message.type === "MENU_UPDATED") {
          console.log("🍽️ [GLOBAL] MENU_UPDATED detected - global menu update");
        } else if (message.type === "MENU_UPDATE_FAILED") {
          console.log(
            "❌ [GLOBAL] MENU_UPDATE_FAILED detected - menu update failed"
          );
        } else if (message.type === "ERROR_NOTIFICATION") {
          console.log("🚨 [GLOBAL] ERROR_NOTIFICATION detected - system error");
          setOrderRefreshTrigger((prev) => prev + 1);

          const isAr = String(i18nLanguageRef.current || "")
            .toLowerCase()
            .startsWith("ar");
          const localized = resolveLocalizedWsMessage(
            message.data,
            i18nLanguageRef.current
          );
          const orderRef =
            message.data?.order_id || message.data?._id || "";
          const isCancelled =
            message.data?.error_type === "TRACKHUB_SHIPMENT_CANCELLED";
          const title = isCancelled
            ? t(
                "trackHubShipmentCancelledTitle",
                "TrackHub shipment cancelled"
              )
            : message.data?.error_type === "TRACKHUB_SHIPMENT_FAILED"
              ? t(
                  "trackHubShipmentFailedTitle",
                  "TrackHub shipment failed"
                )
              : t("error", "Error");
          const body =
            localized ||
            formatTrackHubError(
              {
                lastErrorCode: message.data?.error_code,
                lastErrorMessage: {
                  en: message.data?.message,
                  ar: message.data?.message_ar,
                },
                lastError: message.data?.message || message.data?.raw,
              },
              { t, isRTL: isAr, language: i18nLanguageRef.current }
            ) ||
            (isCancelled
              ? t("shipmentCancelled", "Shipment was cancelled")
              : t("shipmentFailed", "Shipment failed"));
          const toastText = orderRef
            ? `${title}: #${orderRef} — ${body}`
            : `${title}: ${body}`;

          const toastFn = isCancelled ? toast.warn : toast.error;
          toastFn(toastText, {
            autoClose: 10000,
            toastId: `trackhub-${isCancelled ? "cancel" : "err"}-${message.data?.error_code || "generic"}-${orderRef}`,
          });
        } else if (message.type === "STORES_UPDATED") {
          console.log("🏪 [GLOBAL] STORES_UPDATED detected - store list updated");
        } else if (message.type === "AUTO_TIME_OUT") {
          const timeoutOrder = {
            ...message.data.order,
            is_timeout: true,
            timeout_reason: message.data.reason,
            timeout_platform: message.data.platform,
            timeout_timestamp: message.timestamp,
          };

          setOrderRefreshTrigger((prev) => prev + 1);

          if (
            showNotificationRef.current &&
            isHighPriorityNewOrderPayload(newOrderRef.current)
          ) {
            notificationQueueRef.current.push(timeoutOrder);
            console.log(
              "📋 [GLOBAL] AUTO_TIME_OUT queued behind NEW_ORDER, depth:",
              notificationQueueRef.current.length
            );
            return;
          }

          bumpNotificationMount();
          setNewOrder(timeoutOrder);
          setShowNotification(true);
        } else if (message.type === "INVENTORY_WARNING") {
          console.log("⚠️ [GLOBAL] INVENTORY_WARNING detected - inventory warning");
          const inventoryPayload = {
            is_inventory_warning: true,
            ...message.data,
          };

          setOrderRefreshTrigger((prev) => prev + 1);

          if (
            showNotificationRef.current &&
            isHighPriorityNewOrderPayload(newOrderRef.current)
          ) {
            notificationQueueRef.current.push(inventoryPayload);
            console.log(
              "📋 [GLOBAL] INVENTORY_WARNING queued behind NEW_ORDER, depth:",
              notificationQueueRef.current.length
            );
            return;
          }

          bumpNotificationMount();
          setNewOrder(inventoryPayload);
          setShowNotification(true);
        }
      } catch (error) {
        console.error("❌ [GLOBAL] Error parsing WebSocket message:", error);
      }
    };

        ws.onerror = (error) => {
          console.error("❌ [GLOBAL] WebSocket error:", error);
        };

        ws.onclose = (event) => {
          if (generation !== connectGenerationRef.current) return;

          console.log(
            "🔌 [GLOBAL] WebSocket connection closed:",
            event.code,
            event.reason,
            "wasOpen:",
            Boolean(connectionTimeRef.current),
          );

          const hadOpened = Boolean(connectionTimeRef.current);
          connectionTimeRef.current = null;

          if (intentionalCloseRef.current || !isLoggedIn()) {
            return;
          }

          const refreshSession = !hadOpened || isAuthCloseCode(event.code);
          scheduleReconnect({
            authRetry: refreshSession,
            refreshSession,
          });
        };
      } catch (error) {
        console.error("❌ [GLOBAL] WebSocket connection failed:", error);
        if (generation === connectGenerationRef.current && isLoggedIn()) {
          scheduleReconnect({ refreshSession: true });
        }
      }
    };

    connectWebSocket();

    return () => {
      intentionalCloseRef.current = true;
      clearReconnectTimers();
      console.log("🔌 [GLOBAL] Closing WebSocket connection");
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        ws.onclose = null;
        ws.close(1000, "Component cleanup");
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [
    WS_URL,
    wsKey,
    bumpNotificationMount,
    playNewOrderSound,
    clearReconnectTimers,
    scheduleReconnect,
    isLoggedIn,
    t,
  ]);

  const handleViewOrder = useCallback(() => {
    console.log("👁️  [GLOBAL] View Orders page clicked");

    const orderId = newOrder?.order_id || newOrder?._id;

    if (location.pathname === "/orders-display") {
      console.log(
        "👁️  [GLOBAL] On orders-display page - notification dismissed"
      );
      advanceQueueAfterCurrent();
      return;
    }

    if (location.pathname === "/orders") {
      console.log(
        "👁️  [GLOBAL] On orders page - triggering popup for order:",
        orderId
      );
      setTriggerOrderPopup(orderId);
    } else if (orderId) {
      console.log(
        "👁️  [GLOBAL] On different page - opening orders in new tab with order:",
        orderId
      );
      window.open(`/orders?orderId=${orderId}`, "_blank");
    }

    advanceQueueAfterCurrent();
  }, [newOrder, location.pathname, advanceQueueAfterCurrent]);

  const handleDismiss = useCallback(() => {
    console.log("❌ [GLOBAL] Notification dismissed");
    advanceQueueAfterCurrent();
  }, [advanceQueueAfterCurrent]);

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem("orderNotificationSound", String(newValue));
    if (!newValue && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
  };

  useEffect(() => {
    if (!showNotification) return undefined;
    console.log("⏱️  [GLOBAL] Starting 10-second auto-dismiss timer");
    const timer = setTimeout(() => {
      console.log("⏱️  [GLOBAL] Auto-dismissing notification after 10 seconds");
      advanceQueueAfterCurrent();
    }, 10000);

    return () => clearTimeout(timer);
  }, [showNotification, advanceQueueAfterCurrent]);

  const value = {
    newOrder,
    showNotification,
    orderRefreshTrigger,
    triggerOrderPopup,
    setTriggerOrderPopup,
    handleViewOrder,
    handleDismiss,
    soundEnabled,
    toggleSound,
    ensureConnection,
  };

  const isOrdersDisplayFullscreen =
    location.pathname === "/orders-display" && !!document.fullscreenElement;

  return (
    <OrderNotificationContext.Provider value={value}>
      {children}

      {showNotification && newOrder && !isOrdersDisplayFullscreen && (
        <GlobalOrderNotification
          key={notificationMountKey}
          order={newOrder}
          onView={handleViewOrder}
          onDismiss={handleDismiss}
          hideViewButton={location.pathname === "/orders-display"}
        />
      )}
    </OrderNotificationContext.Provider>
  );
};

export default OrderNotificationProvider;
